import { useEffect, useRef } from 'react';
import { useFestivalSettings } from '@/services/festivalService';

export function FestivalLamp() {
  return <svg viewBox="0 0 80 54" fill="none" focusable="false" aria-hidden="true">
    <path d="M40 3c-1 9-10 11-7 20 3 9 16 8 17 0 1-7-8-12-10-20Z" fill="#e7ab30" />
    <path d="M41 14c0 5-6 7-4 11 2 4 7 3 8-1 1-3-3-6-4-10Z" fill="#fff3bb" />
    <path d="M7 32h66c-5 24-59 24-66 0Z" fill="#a95829" />
    <path d="M7 32c15 8 51 8 66 0M17 42c15 6 34 6 47 0" stroke="#e5b75f" strokeWidth="2" />
    <path d="m28 38 4 4 4-4m9 0 4 4 4-4" stroke="#f4d595" strokeWidth="1.3" />
  </svg>;
}
function Flowerpot() {
  return <svg viewBox="0 0 100 80" fill="none" focusable="false" aria-hidden="true">
    <path d="m50 9-21 53c-2 7 44 7 42 0L50 9Z" fill="#a85729" />
    <path d="m50 9 16 54c-10 3-20 3-30 0L50 9Z" fill="#c98434" />
    <ellipse cx="50" cy="63" rx="23" ry="6" fill="#9b4c23" stroke="#d3a752" />
    <path d="m41 31 18 1m-23 14 28 1m-30 10 33 1" stroke="#f6d68b" strokeWidth="2" />
    <path d="m46 39 4-4 4 4-4 4-4-4Zm-5 13 4-3 4 3-4 3-4-3Zm12 0 4-3 4 3-4 3-4-3Z" fill="#ffe5a2" />
    <path d="M50 9V5" stroke="#85411e" strokeWidth="2" strokeLinecap="round" />
    <ellipse cx="50" cy="70" rx="32" ry="4" fill="#b47429" opacity=".1" />
  </svg>;
}
type Spark = { x: number; y: number; vx: number; vy: number; age: number; life: number; size: number; star: boolean };

export function FestivalFountains() {
  const { data } = useFestivalSettings();
  const wrapper = useRef<HTMLDivElement>(null), canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const host = wrapper.current, surface = canvas.current;
    if (!data?.enabled || !data.effectsEnabled || !host || !surface) return;
    const context = surface.getContext('2d'); if (!context) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let width = 0, height = 0, compact = false, visible = false, frame = 0, previous = 0, emitted = 0;
    let sparks: Spark[] = [];
    const stop = () => { cancelAnimationFrame(frame); frame = 0; previous = 0; emitted = 0; sparks = []; context.clearRect(0, 0, width, height); };
    const draw = (now: number) => {
      const elapsed = previous ? now - previous : 34;
      if (elapsed < 32) { frame = requestAnimationFrame(draw); return; }
      previous = now;
      const dt = Math.min(elapsed, 48); emitted += dt;
      context.clearRect(0, 0, width, height);
      if (emitted > (compact ? 95 : 65)) {
        emitted = 0;
        const edge = compact ? 52 : Math.min(130, width * .075);
        for (const x of [edge, width - edge]) if (sparks.length < (compact ? 32 : 64)) {
          const angle = -Math.PI / 2 + (Math.random() - .5) * 1.05;
          const speed = (compact ? .16 : .24) * (.65 + Math.random() * .5);
          sparks.push({ x, y: height - (compact ? 70 : 81), vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, age: 0, life: 650 + Math.random() * 450, size: .7 + Math.random() * 1.3, star: Math.random() < .12 });
        }
      }
      sparks = sparks.filter(p => p.age < p.life);
      for (const p of sparks) {
        p.age += dt; p.x += p.vx * dt; p.y += p.vy * dt; p.vy += .00025 * dt;
        context.globalAlpha = Math.max(0, 1 - p.age / p.life) * .85;
        context.strokeStyle = '#bf7916'; context.fillStyle = '#edba49'; context.shadowColor = '#efb94f'; context.shadowBlur = 3;
        const size = p.size * (1 - p.age / p.life * .5);
        context.beginPath();
        if (p.star) { context.moveTo(p.x - size * 2, p.y); context.lineTo(p.x + size * 2, p.y); context.moveTo(p.x, p.y - size * 2); context.lineTo(p.x, p.y + size * 2); context.lineWidth = .8; context.stroke(); }
        else { context.arc(p.x, p.y, size, 0, Math.PI * 2); context.fill(); }
      }
      context.globalAlpha = 1; frame = requestAnimationFrame(draw);
    };
    const resume = () => { stop(); if (visible && !document.hidden && !motion.matches) frame = requestAnimationFrame(draw); };
    const resize = () => {
      width = host.clientWidth; height = host.clientHeight; compact = width < 1024;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      surface.width = Math.round(width * ratio); surface.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0); resume();
    };
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; resume(); }, { threshold: .05 });
    const sizing = new ResizeObserver(resize); sizing.observe(host); observer.observe(host); resize();
    document.addEventListener('visibilitychange', resume); motion.addEventListener('change', resume);
    return () => { stop(); observer.disconnect(); sizing.disconnect(); document.removeEventListener('visibilitychange', resume); motion.removeEventListener('change', resume); };
  }, [data?.enabled, data?.effectsEnabled]);
  if (!data?.enabled) return null;
  return <div ref={wrapper} className="festival-fountains" aria-hidden="true">
    <canvas ref={canvas} />
    <span className="festival-flowerpot left"><Flowerpot /></span>
    <span className="festival-flowerpot right"><Flowerpot /></span>
    <span className="festival-intro-lamp"><FestivalLamp /></span>
  </div>;
}
